import { useCallback } from 'react';
import { differenceInCalendarDays } from 'date-fns';
import { plural } from '@frameio/i18n';
import { useAccountPermissions } from '@frameio/permissions';
import { SubscriptionUpdateSource } from '@frameio/plan-management/feature-gate/config';
import { usePlanManagementModalContext } from '@frameio/plan-management/modal/hooks';
import { AlertBanner } from '@frameio/vapor';
import type { AlertBannerProps } from '../types';
import { UpgradeBannerWrapper } from './BannerWrapper';
import TEXT from './generated/TEXT/TrialAlertBanner';

TEXT.DEFINE({
  /** Title showing days remaining in trial */
  title: (remainingDays: number) =>
    `Your Pro plan free trial ends in ${remainingDays} ${plural(remainingDays, {
      one: 'day',
      other: 'days',
    })}.`,

  /** Message for billing managers */
  detailsWithPermission:
    'Subscribe to keep using premium features and higher usage limits.',

  /** Message for non-billing users */
  detailsNoPermission: 'Contact the account owner to upgrade account.',

  /** CTA button text */
  cta: 'Subscribe now',
});

const getRemainingDays = (date: string) => {
  const currentDate = new Date();
  const comparedDate = new Date(date);

  return differenceInCalendarDays(comparedDate, currentDate);
};

const REMAINING_DAYS_BEFORE_ALERT = {
  FIRST: 3,
  LAST: 1,
};

export function TrialAlertBanner({
  account,
  fetchPlansData,
  upgradedPlan,
}: AlertBannerProps) {
  const subscription = account?.subscription;
  const trial = subscription?.trial;
  const isTrialActive = trial?.isActive;
  const promotionExpiresAt = trial?.promotionExpiresAt ?? '';
  const isAccountLocked = !!account?.lockedAt;

  const accountId = account?.id ?? '';
  const canManageBilling =
    useAccountPermissions({
      accountId,
      check: 'canManageBilling',
    }) ?? false;

  const remainingDays = getRemainingDays(promotionExpiresAt);

  const planManagementModalContext = usePlanManagementModalContext();

  const handleClick = useCallback(() => {
    if (!upgradedPlan) return;

    planManagementModalContext?.setSelectedPlanWithFeature({
      ...upgradedPlan,
      subscriptionUpdateSource: SubscriptionUpdateSource.TRIAL_ENDING,
    });
  }, [upgradedPlan, planManagementModalContext]);

  const showBanner =
    (remainingDays === REMAINING_DAYS_BEFORE_ALERT.FIRST ||
      remainingDays === REMAINING_DAYS_BEFORE_ALERT.LAST) &&
    isTrialActive &&
    !isAccountLocked;

  if (!showBanner) return null;

  const details = canManageBilling
    ? TEXT.detailsWithPermission
    : TEXT.detailsNoPermission;

  return (
    <UpgradeBannerWrapper onHoverIntent={fetchPlansData}>
      <AlertBanner
        ctaText={canManageBilling && upgradedPlan ? TEXT.cta : undefined}
        details={details}
        onCtaClick={handleClick}
        title={TEXT.title(remainingDays)}
        variant="notice"
      />
    </UpgradeBannerWrapper>
  );
}
